// Roaring Meteor (owner 2026-11-03: 'Rework Roaring Meteor, very underwhelming, need to feel more Earth shattering power,
// impact and ground deformation/crater'). Render-only: reads sim.dive (the leap; cometDive.ts supplies the hero's height with
// its meteor profile) and the sim.fusion roar bodies, and reacts to the 'diveStart' / 'diveLand' (fuse 'roarfall') and 'fuse'
// ('cast', 'ring') events. It decides nothing about gameplay: the crater, launch, daze, stun and damage live in src/core/fusions.ts.
//  - the leap: a white-hot streak up (cometDive.ts' trail), a charging glow at the apex (a swelling star, sparks drawn in, a
//    light) and a landing telegraph on the ground: a growing target ring with dust lifting off it, pulsing faster as he falls
//  - the crash: the comet shell + fire ribbon (cometDive.ts) and an air shock punched out of the mark just before contact
//  - impact: a huge flash (FLASH budget), a freeze-frame (view.ts hitstop, through the a11y limiter), a camera kick + shake
//    (both scaled by the Shake setting), ground shock rings, a fireball, a dust and rock eruption (particles + Rapier rock clods,
//    debris.ts erupt), a deep bowl with a raised rim stamped over the sim's crater, radial cracks (char lines in the burn map +
//    glowing molten ribbons + ground light that cool over ~5 s) and a dust cloud that lingers ~3 s over the crater
//  - the roars: per ring a dust wall riding the front (the sim's BK.roar bodies), a height ripple rolling through the ground
//    (ground.ts), a bright ring, a flash and a shove of shake; the refractive heat-haze wall and the screen-space ground ripples
//    are the Force Shout wave (forceShout.ts, the 'shout' event the ring also emits)
// Pooled (ribbons, cards); emission is capped per frame; no per-frame allocations.
import * as THREE from 'three/webgpu';
import type { Sim, SimEvent } from '../core/sim';
import { BK, FUSIONS } from '../core/fusions';
import { FLASH } from './a11y';
import type { Fx } from './fx';
import type { GroundDeform } from './ground';
import type { GroundGlow } from './groundGlow';
import { RibbonStrips, RS_STRIDE } from './ribbonStrips';
import { Cards, haloLook } from './skillsBShared';

const HOT = 0xfff1c8, ORANGE = 0xff8a3a, EMBER = 0xff5a1a, PALE = 0xd2ecff, DUST = 0xb89a78, CLOD = 0x6a4a30;
const TAU = Math.PI * 2;
const CRACKS = 16, CRACK_PTS = 9;
/** molten cracks cool over this long (the char lines in the burn map stay and heal like other scars) */
export const CRACK_LIFE = 5.5;
/** the dust cloud over the crater keeps rising this long */
export const DUST_LIFE = 3.2;
/** crater depth multiplier over the sim's crater (ground.ts stampCrater `deep`; the bowl is capped at MAX_DEPTH, the rim at MAX_RAISE) */
export const CRATER_DEEP = 1.6;
/** rock clods thrown out of the crater (Rapier pieces) and their launch speed */
export const CLODS = 14, CLOD_SPEED = 13;

type Ev<T extends SimEvent['t']> = Extract<SimEvent, { t: T }>;
interface Crack { on: boolean; t: number; life: number; w: number; n: number; p: Float32Array }
const _flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
const _edge = new THREE.Color(), _core = new THREE.Color(), _tmp = new THREE.Color(), _c = new THREE.Color();
const rgb = (hex: number, k = 1): [number, number, number] => { _c.setHex(hex); return [_c.r * k, _c.g * k, _c.b * k]; };

export class RoarFallFx {
  /** freeze-frame and camera kick the view should apply (taken each frame) */
  hitstop = 0; kick = 0;
  /** QA counters */
  lands = 0; rings = 0; casts = 0;
  private air = false; private tl = 0; private dur = 0.78; private x1 = 0; private z1 = 0; private R = 5.5;
  private shocked = false; private acc = 0; private starAcc = 0; private u = 0;
  private ribbons: RibbonStrips; private cracks: Crack[] = []; private halos: Cards;
  private cloud = { on: false, t: 0, x: 0, z: 0, r: 0 };
  private crater = { on: false, t: 0, x: 0, z: 0, r: 0 };

  constructor(scene: THREE.Scene, private ground: GroundDeform, private fx: Fx, private glow: GroundGlow, private erupt: (x: number, z: number, r: number, n: number, power: number) => void) {
    this.ribbons = new RibbonStrips(scene, CRACKS, CRACK_PTS + 1, false, 2);
    for (let i = 0; i < CRACKS; i++) this.cracks.push({ on: false, t: 9, life: 1, w: 0.2, n: 0, p: new Float32Array(CRACK_PTS * 3) });
    this.halos = new Cards(scene, 4, haloLook(ORANGE, 0.9), 3);
  }

  // ------------------------------------------------------------------ events
  event(ev: SimEvent, sim: Sim): void {
    if (ev.t === 'diveStart') { if (sim.dive.fx === 'roarfall') this.start(ev, sim); return; }
    if (ev.t === 'diveLand') { if (ev.fuse === 'roarfall') this.land(ev); return; }
    if (ev.t !== 'fuse' || ev.f !== 'roarfall') return;
    if (ev.k === 'cast') this.cast(ev); else if (ev.k === 'ring') this.ring(ev, sim);
    // 'impact' arrives in the same tick as diveLand, which carries the crater radius: the landing is drawn from diveLand
  }

  private start(ev: Ev<'diveStart'>, sim: Sim): void {
    this.air = true; this.tl = 0; this.dur = ev.dur; this.x1 = ev.x1; this.z1 = ev.z1; this.shocked = false; this.acc = 1; this.starAcc = 1; this.u = 0;
    this.R = FUSIONS.roarfall.n.r * sim.areaMul;
  }

  /** take-off: a white column of light and speed lines straight up, a shock ring at the feet */
  private cast(ev: Ev<'fuse'>): void {
    this.casts++;
    const fx = this.fx, gy = this.ground.sample(ev.x, ev.z);
    fx.stars.add(ev.x, gy + 1.3, ev.z, 2.4, 0.14, 0xffffff, 2.6);
    fx.rings.add(ev.x, gy + 0.15, ev.z, 3.2, 0.32, PALE, 2.2, 1.1);
    for (let k = 0; k < 18; k++) fx.particles.emitDir(ev.x + (Math.random() - 0.5) * 0.9, gy + 0.4 + Math.random() * 0.8, ev.z + (Math.random() - 0.5) * 0.9, (Math.random() - 0.5) * 1.5, 11 + Math.random() * 8, (Math.random() - 0.5) * 1.5, k % 3 ? 0xffffff : HOT, 0.18, 0.35, 2.8);
    fx.lights.flash(ev.x, gy + 1.5, ev.z, HOT, 22 * Math.max(0.3, FLASH.dim), 0.2, 12);
    this.glow.flash(ev.x, ev.z, 2.6, HOT, 0.6, 0.22);
    fx.shake = Math.min(1, fx.shake + 0.1);
  }

  /** the landing: flash, freeze, kick, shock rings, fireball, dust + rock eruption, the crater, cracks, the lingering cloud */
  private land(ev: Ev<'diveLand'>): void {
    this.lands++; this.air = false;
    const fx = this.fx, x = ev.x, z = ev.z, R = ev.r, cr = ev.cr, gy = this.ground.sample(x, z), kf = FLASH.gate(true);
    // the flash: a white-hot core, an orange bloom, a screen-lighting point light and ground light (all in the flash budget)
    if (kf > 0) fx.flashes.add(x, gy + 1.0, z, R * 0.6, 0.2, HOT, 1.6 * kf);
    fx.stars.add(x, gy + 1.2, z, R * 2.2, 0.2, 0xffffff, 3.2);
    fx.stars.add(x, gy + 1.0, z, R * 3.2, 0.36, ORANGE, 2.0);
    fx.lights.flash(x, gy + 2, z, ORANGE, 90 * Math.max(0.3, kf), 0.45, R * 5);
    this.glow.flash(x, z, R * 2.2, ORANGE, 1.2, 0.6); this.glow.flash(x, z, R * 1.2, 0xffffff, 1.5, 0.2); this.glow.flash(x, z, R * 0.9, HOT, 1.1, 0.35);
    this.hitstop = Math.max(this.hitstop, 0.1); this.kick = 1; fx.shake = 1;
    // ground-level shock rings racing out, then a slower wide dust ring
    fx.rings.add(x, gy + 0.3, z, R * 0.8, 0.2, 0xffffff, 3.2, 1.1);
    fx.rings.add(x, gy + 0.15, z, R * 1.4, 0.38, HOT, 3.4, 1.8);
    fx.rings.add(x, gy + 0.2, z, R * 2.2, 0.62, ORANGE, 2.4, 2.2);
    fx.rings.add(x, gy + 0.12, z, R * 3.0, 0.95, DUST, 1.4, 2.8);
    fx.blast?.blast(x, gy + 0.8, z, R * 0.7, true, ORANGE);
    // the eruption: a dust column, a dust curtain rolling off the rim, clods and chips, Rapier rock pieces
    for (let k = 0; k < 22; k++) { const a = Math.random() * TAU, rr = Math.random() * cr * 0.5, sp = 1 + Math.random() * 2.5; fx.blast?.dust(x + Math.cos(a) * rr, gy + 0.4, z + Math.sin(a) * rr, Math.cos(a) * sp, 7 + Math.random() * 7, Math.sin(a) * sp, 1.6 + Math.random() * 1.2, 0.9, 2.6 + Math.random() * 1.2, 0.1); }
    for (let k = 0; k < 36; k++) { const a = k / 36 * TAU + Math.random() * 0.2, rr = cr * 0.75; fx.blast?.dust(x + Math.cos(a) * rr, gy + 0.3, z + Math.sin(a) * rr, Math.cos(a) * R * 3.0, 0.8 + Math.random() * 1.4, Math.sin(a) * R * 3.0, 1.1 + Math.random() * 0.6, 0.8, 2.4 + Math.random() * 1.4, 0); }
    fx.blast?.pebbles(x, gy + 0.5, z, 40, 0, 11, 0, R * 1.7, 0.1, CLOD, 2.4);
    fx.blast?.pebbles(x, gy + 0.6, z, 20, 0, 15, 0, R * 0.8, 0.07, 0x8a6a48, 2.8);
    for (let k = 0; k < 12; k++) { const a = k / 12 * TAU; fx.blast?.chips(x + Math.cos(a) * cr * 0.6, z + Math.sin(a) * cr * 0.6, Math.cos(a), Math.sin(a), 2); }
    for (let k = 0; k < 30; k++) { const a = Math.random() * TAU, sp = 5 + Math.random() * 10; fx.particles.emitDir(x, gy + 0.5, z, Math.cos(a) * sp, 4 + Math.random() * 9, Math.sin(a) * sp, k % 3 ? EMBER : HOT, 0.24, 0.6 + Math.random() * 0.5, 2.6); }
    this.erupt(x, z, cr, CLODS, CLOD_SPEED);
    // the crater: a deep bowl with a raised rim over the sim's crater, a charred floor, radial cracks (char + molten glow)
    this.ground.stampCrater(x, z, cr, CRATER_DEEP);
    this.ground.stampBurn(x, z, cr * 0.85);
    const n = CRACKS - 2 + Math.floor(Math.random() * 3), a0 = Math.random() * TAU;
    for (let k = 0; k < n; k++) {
      const a = a0 + k / n * TAU + (Math.random() - 0.5) * 0.35, r1 = R * (1.25 + Math.random() * 0.9), w = 0.34 + Math.random() * 0.26;
      this.crack(x, z, a, cr * 0.5, r1, w, CRACK_LIFE * (0.85 + Math.random() * 0.3));
    }
    Object.assign(this.cloud, { on: true, t: 0, x, z, r: cr * 1.1 });
    Object.assign(this.crater, { on: true, t: 0, x, z, r: cr });
  }

  /** a jagged crack from (x, z) along angle a, from r0 to r1 metres out: a molten ribbon on the ground and a char line in the burn map */
  private crack(x: number, z: number, a: number, r0: number, r1: number, w: number, life: number): void {
    const c = this.cracks.reduce((p, q) => (!q.on ? (p.on ? q : p) : !p.on ? p : q.t > p.t ? q : p));
    c.on = true; c.t = 0; c.life = life; c.w = w; c.n = CRACK_PTS;
    let aa = a;
    for (let i = 0; i < CRACK_PTS; i++) {
      const u = i / (CRACK_PTS - 1), r = r0 + (r1 - r0) * u; aa += (Math.random() - 0.5) * 0.4;
      const px = x + Math.cos(aa) * r, pz = z + Math.sin(aa) * r;
      c.p[i * 3] = px; c.p[i * 3 + 1] = this.ground.sample(px, pz) + 0.08; c.p[i * 3 + 2] = pz;
    }
    // the char line: small burn stamps along the crack (the burn map's soft shading reads them as a scorch seam)
    for (let i = 1; i < CRACK_PTS; i++) {
      const x0 = c.p[(i - 1) * 3], z0 = c.p[(i - 1) * 3 + 2], x1 = c.p[i * 3], z1 = c.p[i * 3 + 2], L = Math.hypot(x1 - x0, z1 - z0);
      for (let s = 0; s < L; s += 0.5) { const t = s / L; this.ground.stampBurn(x0 + (x1 - x0) * t, z0 + (z1 - z0) * t, 0.75 * (1 - 0.4 * i / CRACK_PTS)); }
    }
  }

  /** a roar ring sets out (ev.dx = ring index): a flash and a bright ring at the centre, the ground ripple, a shove of shake */
  private ring(ev: Ev<'fuse'>, sim: Sim): void {
    this.rings++;
    const fx = this.fx, k = ev.dx, gy = this.ground.sample(ev.x, ev.z), speed = 30 * sim.pace;
    fx.stars.add(ev.x, gy + 1.3, ev.z, 3.6 - k * 0.6, 0.12, PALE, 2.6);
    fx.rings.add(ev.x, gy + 0.22, ev.z, ev.r * 1.08, ev.r / speed + 0.15, PALE, 2.4, 1.8);
    fx.rings.add(ev.x, gy + 0.3, ev.z, ev.r * 0.5, 0.2, 0xffffff, 2.6, 0.9);
    this.glow.flash(ev.x, ev.z, ev.r * 0.6, PALE, 0.55, 0.25);
    if (FLASH.dim >= 1) fx.lights.flash(ev.x, gy + 1.5, ev.z, PALE, 26, 0.15, 16);
    this.ground.ripple(ev.x, ev.z, speed, ev.r, k === 0 ? 0.26 : 0.18, 1.7);
    fx.shake = Math.min(1, fx.shake + (k === 0 ? 0.3 : 0.2)); this.hitstop = Math.max(this.hitstop, 0.03);
  }

  // ------------------------------------------------------------------ per frame
  /** hx/hz = the hero's drawn ground position, lift = his height above it (cometDive.ts) */
  update(dt: number, sim: Sim, cam: THREE.Camera, fx: Fx, hx: number, hz: number, lift: number): void {
    const d = sim.dive;
    if (d.active && d.fx === 'roarfall' && !this.air) { this.air = true; this.tl = d.t; this.dur = d.dur; this.x1 = d.x1; this.z1 = d.z1; this.R = FUSIONS.roarfall.n.r * sim.areaMul; }
    if (!d.active || d.fx !== 'roarfall') this.air = false;
    this.halos.begin();
    if (this.air) {
      this.tl = Math.min(this.dur, this.tl + dt);
      const u = this.u = this.tl / Math.max(1e-3, this.dur), gy1 = this.ground.sample(this.x1, this.z1), R = this.R;
      // the landing telegraph: a target ring growing to the crater size, pulsing faster as the crash nears
      const grow = Math.min(1, u / 0.85), tr = R * (0.25 + 0.75 * grow), pulse = 0.55 + 0.45 * Math.sin(this.tl * (14 + 36 * u));
      this.halos.push(this.x1, gy1 + 0.22, this.z1, tr * 2.3, _flat, 0.45 * pulse + 0.25 * grow, 0, ...rgb(ORANGE, 1.2));
      this.acc += dt;
      if (this.acc > 0.11) {
        this.acc = 0;
        fx.rings.add(this.x1, gy1 + 0.18, this.z1, tr * 1.05, 0.4, ORANGE, 1.6 + 1.6 * grow, 0.8);
        for (let k = 0; k < 3; k++) { const a = Math.random() * TAU, rr = Math.sqrt(Math.random()) * tr; fx.blast?.dust(this.x1 + Math.cos(a) * rr, gy1 + 0.2, this.z1 + Math.sin(a) * rr, (Math.random() - 0.5) * 0.6, 0.9 + u * 1.6, (Math.random() - 0.5) * 0.6, 1.0, 0.45, 1.1 + u, 0); }
        for (let k = 0; k < 2; k++) fx.particles.emitDir(this.x1 + (Math.random() - 0.5) * tr * 1.6, gy1 + 0.1, this.z1 + (Math.random() - 0.5) * tr * 1.6, 0, 2.5 + Math.random() * 3, 0, DUST, 0.12, 0.7, 1.2);
      }
      // the apex: a charging glow on the hero (a swelling star, sparks drawn in, a flicker of light)
      const hy = this.ground.sample(hx, hz) + lift + 1.0;
      if (u > 0.36 && u < 0.56) {
        const k = Math.min(1, (u - 0.36) / 0.12);
        this.starAcc += dt;
        if (this.starAcc > 0.05) { this.starAcc = 0; fx.stars.add(hx, hy, hz, 1.2 + 2.4 * k, 0.12, k > 0.6 ? HOT : ORANGE, 2.2 + k); }
        for (let j = 0; j < 3; j++) { const a = Math.random() * TAU, rr = 1.6 + Math.random() * 1.4, ya = (Math.random() - 0.5) * 1.6; fx.particles.emitDir(hx + Math.cos(a) * rr, hy + ya, hz + Math.sin(a) * rr, -Math.cos(a) * 9, -ya * 4, -Math.sin(a) * 9, j ? ORANGE : HOT, 0.16, 0.22, 2.6); }
        if (FLASH.dim >= 1 && Math.random() < 0.25) fx.lights.flash(hx, hy, hz, ORANGE, 14 * k, 0.08, 10);
      }
      // the air shock just before contact: a flat white ring and a dust ring punched out from the mark, a pop under the hero
      if (u >= 0.9 && !this.shocked) {
        this.shocked = true;
        fx.rings.add(this.x1, gy1 + 0.3, this.z1, R * 0.9, 0.22, 0xffffff, 2.8, 1.2); fx.rings.add(this.x1, gy1 + 0.2, this.z1, R * 1.3, 0.35, PALE, 1.6, 2);
        for (let k = 0; k < 16; k++) { const a = k / 16 * TAU; fx.blast?.dust(this.x1 + Math.cos(a) * R * 0.4, gy1 + 0.25, this.z1 + Math.sin(a) * R * 0.4, Math.cos(a) * R * 1.8, 0.4, Math.sin(a) * R * 1.8, 0.6, 0.5, 1.2, 0); }
        fx.stars.add(hx, hy - 0.5, hz, 2.5, 0.1, 0xffffff, 2.5);
        fx.shake = Math.min(1, fx.shake + 0.15);
      }
    }
    this.halos.end();
    // the roars: a dust wall riding every front (the sim's roar bodies: d = the front's radius), with clods kicked up
    for (const b of sim.fusion.bodies) {
      if (!b.alive || b.kind !== BK.roar || b.f !== 'roarfall' || b.t < 0 || b.d <= 0.5) continue;
      const n = b.k === 0 ? 7 : 5;
      for (let k = 0; k < n; k++) {
        const a = Math.random() * TAU, px = b.x + Math.cos(a) * b.d, pz = b.z + Math.sin(a) * b.d, g = this.ground.sample(px, pz);
        fx.blast?.dust(px, g + 0.2 + Math.random() * 0.9, pz, Math.cos(a) * 9, 1.2 + Math.random() * 1.6, Math.sin(a) * 9, 0.7 + Math.random() * 0.4, 0.7, 1.9, 0);
        if (k < 2) fx.blast?.pebbles(px, g + 0.2, pz, 1, Math.cos(a) * 5, 4 + Math.random() * 3, Math.sin(a) * 5, 1.5, 0.06, CLOD, 1.2);
      }
    }
    // the dust cloud lingering over the crater
    if (this.cloud.on) {
      const c = this.cloud; c.t += dt;
      if (c.t > DUST_LIFE) c.on = false;
      else { const k = 1 - c.t / DUST_LIFE; if (Math.random() < 0.7 * k + 0.1) { const a = Math.random() * TAU, rr = Math.sqrt(Math.random()) * c.r, px = c.x + Math.cos(a) * rr, pz = c.z + Math.sin(a) * rr; fx.blast?.dust(px, this.ground.sample(px, pz) + 0.3 + Math.random() * 1.2, pz, (Math.random() - 0.5) * 1.2, 0.35 + Math.random() * 0.5, (Math.random() - 0.5) * 1.2, 2.6 + Math.random() * 1.6, 1.0, 2.6 + Math.random() * 1.4, 0.15); } }
    }
    if (this.crater.on) { this.crater.t += dt; if (this.crater.t > CRACK_LIFE + 1) this.crater.on = false; }
    // the molten cracks: white-hot cores over ember edges that cool to dark seams
    const Rb = this.ribbons, q = Rb.pts; Rb.begin();
    for (const c of this.cracks) {
      if (!c.on) continue;
      c.t += dt; if (c.t > c.life) { c.on = false; continue; }
      const k = c.t / c.life, cool = Math.min(1, k * 1.15) ** 0.8, a = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3, flick = 0.85 + 0.15 * Math.sin(c.t * 23 + c.p[0]);
      for (let i = 0; i < c.n; i++) { const o = i * RS_STRIDE, s = i * 3, u2 = i / Math.max(1, c.n - 1); q[o] = c.p[s]; q[o + 1] = c.p[s + 1]; q[o + 2] = c.p[s + 2]; q[o + 3] = c.w * (1 - u2 * 0.75) * (0.7 + 0.3 * (1 - k)); q[o + 4] = a; }
      _edge.setHex(EMBER).lerp(_tmp.setHex(0x3a1a10), cool).multiplyScalar((1.7 * (1 - cool) + 0.35) * flick);
      _core.setHex(HOT).lerp(_tmp.setHex(0x5a2010), cool).multiplyScalar((2.0 * (1 - cool * 0.85) + 0.2) * flick);
      Rb.commit(c.n, cam.position, _edge, _core, 0.45);
    }
    Rb.end();
  }

  /** ground light: the crater's molten glow and the cracks cooling, the charge under the hero at the apex */
  glowPools(g: GroundGlow, hx: number, hz: number, lift: number): void {
    if (this.crater.on) {
      const c = this.crater, k = Math.max(0, 1 - c.t / CRACK_LIFE) ** 1.4;
      if (k > 0.01) g.blob(c.x, this.ground.sample(c.x, c.z) + 0.4, c.z, c.r * 1.1, EMBER, 0.9 * k, 0.7);
    }
    for (const c of this.cracks) {
      if (!c.on) continue;
      const k = c.t / c.life, cool = Math.min(1, k * 1.15) ** 0.8, s = (c.n - 1) * 3;
      if (cool >= 0.98) continue;
      g.strip(c.p[0], c.p[2], c.p[s], c.p[s + 2], c.p[1] + 0.1, 0.55, EMBER, 0.8 * (1 - cool), 0.6, 0.6);
    }
    if (this.air && this.u > 0.36 && this.u < 0.58) g.blob(hx, this.ground.sample(hx, hz) + lift + 1, hz, 3, ORANGE, 0.7);
  }

  clear(): void {
    this.air = false; this.u = 0; this.cloud.on = false; this.crater.on = false; this.hitstop = 0; this.kick = 0;
    for (const c of this.cracks) c.on = false; this.ribbons.clear(); this.halos.begin(); this.halos.end();
  }
}
